import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  alpha,
} from '@mui/material';
import { VerifiedUser, Security } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface BackendSecurityTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const BackendSecurityTab: React.FC<BackendSecurityTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Backend Security"
      emoji="🔒"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Backend Security Features
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderLeft: `4px solid ${props.goldColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Authentication & Authorization
              </Typography>
              <List dense>
                {[
                  'JWT Tokens - Secure session management',
                  'Passport.js - Local & Google OAuth',
                  'Role-based Access Control - User/Admin roles',
                  'Session Management - Secure session handling',
                  'Password Security - bcrypt hashing',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <VerifiedUser sx={{ color: props.goldColor }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderLeft: `4px solid #0F3B2E` }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Protection Layers
              </Typography>
              <List dense>
                {[
                  'Input Validation - express-validator',
                  'CORS Protection - Configured origins',
                  'Rate Limiting - Abuse protection',
                  'File Upload Security - Size & type limits',
                  'SQL Injection Prevention - Mongoose queries',
                ].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Security sx={{ color: '#0F3B2E' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
          Security Implementation
        </Typography>
        <Grid container spacing={2}>
          {[
            { title: 'Environment Variables', status: 'Secure', color: props.goldColor },
            { title: 'API Keys', status: 'Encrypted', color: '#0F3B2E' },
            { title: 'Database Connection', status: 'SSL Enabled', color: '#1D5C4A' },
            { title: 'File Uploads', status: 'Validated', color: props.goldColor },
            { title: 'Error Handling', status: 'No Leaks', color: '#0F3B2E' },
            { title: 'Logging', status: 'Secure', color: '#1D5C4A' },
          ].map((sec, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
              <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${sec.color}` }}>
                <Typography variant="caption" color={props.colors.textSecondary}>
                  {sec.title}
                </Typography>
                <Typography variant="h6" sx={{ color: sec.color }}>
                  {sec.status}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </BaseTab>
  );
};

export default BackendSecurityTab;